import React from 'react';
import { Campaign } from '../../types/Campaign';
import { CalendarIcon, DollarSignIcon, TagIcon, PercentIcon, ArrowRightIcon } from 'lucide-react';
interface CampaignListProps {
  campaigns: Campaign[];
  onCampaignClick: (campaignId: string) => void;
}
export function CampaignList({
  campaigns,
  onCampaignClick
}: CampaignListProps) {
  // Function to calculate budget usage percentage
  const calculateBudgetUsage = (campaign: Campaign) => {
    if (campaign.budget === 0) return 0;
    return Math.round(campaign.budgetUsed / campaign.budget * 100);
  };
  // Function to determine status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="overflow-x-auto" data-id="element-422">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-423">
        <thead className="bg-gray-50" data-id="element-424">
          <tr data-id="element-425">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-426">
              Campaign
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-427">
              Code
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-428">
              Discount
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-429">
              Budget Usage
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-430">
              Expiry
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-431">
              Status
            </th>
            <th scope="col" className="relative px-6 py-3" data-id="element-432">
              <span className="sr-only" data-id="element-433">View</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-434">
          {campaigns.length > 0 ? campaigns.map(campaign => <tr key={campaign.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => onCampaignClick(campaign.id)} data-id="element-435">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-436">
                  <div className="flex items-center" data-id="element-437">
                    <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-438">
                      <TagIcon size={18} className="text-blue-600" data-id="element-439" />
                    </div>
                    <div className="ml-4" data-id="element-440">
                      <div className="text-sm font-medium text-gray-900" data-id="element-441">
                        {campaign.title}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-442">
                        {campaign.category}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-443">
                  <div className="text-sm font-medium text-gray-900" data-id="element-444">
                    {campaign.code}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-445">
                    {campaign.codeStrategy}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-446">
                  <div className="flex items-center text-sm text-gray-900" data-id="element-447">
                    {campaign.discountPercentage > 0 ? <>
                        <PercentIcon size={16} className="mr-1 text-purple-500" data-id="element-448" />
                        {campaign.discountPercentage}%
                      </> : <>
                        <DollarSignIcon size={16} className="mr-1 text-green-500" data-id="element-449" />
                        {campaign.fixedDiscountAmount} {campaign.currency}
                      </>}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-450">
                    {campaign.discountCarriedBy}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-451">
                  <div className="flex items-center" data-id="element-452">
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2" data-id="element-453">
                      <div className="bg-blue-600 h-2.5 rounded-full" style={{
                  width: `${calculateBudgetUsage(campaign)}%`
                }} data-id="element-454"></div>
                    </div>
                    <span className="text-sm text-gray-500" data-id="element-455">
                      {calculateBudgetUsage(campaign)}%
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1" data-id="element-456">
                    {campaign.budgetUsed.toLocaleString()} /{' '}
                    {campaign.budget.toLocaleString()} {campaign.currency}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-457">
                  <div className="flex items-center text-sm text-gray-900" data-id="element-458">
                    <CalendarIcon size={16} className="mr-1 text-gray-500" data-id="element-459" />
                    {campaign.expiryDate}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-460">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(campaign.status)}`} data-id="element-461">
                    {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-462">
                  <button className="text-blue-600 hover:text-blue-900" data-id="element-463">
                    <ArrowRightIcon size={16} data-id="element-464" />
                  </button>
                </td>
              </tr>) : <tr data-id="element-465">
              <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-466">
                No campaigns found
              </td>
            </tr>}
        </tbody>
      </table>
    </div>;
}